import { BrowserModule } from '@angular/platform-browser';
import {NgModule, LOCALE_ID, APP_INITIALIZER} from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HashLocationStrategy, LocationStrategy, registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
registerLocaleData(localeDe);

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBarModule, MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule } from '@angular/material/sort';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MAT_DATE_LOCALE, MatRippleModule, provideNativeDateAdapter } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatRadioModule } from '@angular/material/radio';

import { environment } from 'src/environments/environment';
import { Environment, EnvironmentFeatures, Texts } from './classes/environment';
import { DataInput, TrivUIToolsLibModule } from "@trivelope/triv-ui-tools-lib";

import { AuthInterceptor } from './interceptors/authentication.interceptor';
import { AuthBasicGuard } from './guards/auth-basic.guard';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { PageNotFoundComponent } from 'src/app/components/layout/page-not-found.component';
import { FooterComponent } from 'src/app/components/layout/footer.component';
import { LayoutComponent } from 'src/app/components/layout/layout.component';
import { AuthenticationLoginComponent } from './components/authentication/authentication-login/authentication-login.component';
import { ProfileComponent } from './components/profile/profile.component';
import { ChangePasswordComponent } from './components/profile/change-pwd.component';
import { StatisticsComponent } from 'src/app/components/statistics/statistics.component';
import { DataMaintenanceComponent } from 'src/app/components/data-maintenance/data-maintenance.component';
import { MyMemberComponent } from './components/my-member/my-member.component';
import { DocumentTreeComponent } from './components/document-tree/document-tree.component';
import { UserManagementComponent } from 'src/app/components/administration/user-management.component';
import { UserEditorComponent, UserChangePasswordDialog, UserDeleteConfirmationDialog } from './components/editors/user-editor.component';
import { MemberManagementComponent } from 'src/app/components/administration/member-management.component';
import { MemberEditorComponent } from 'src/app/components/editors/member-editor.component';
import { SectionManagementComponent, SectionCreatorDialog } from 'src/app/components/administration/section-management.component';
import { SectionEditorComponent } from 'src/app/components/editors/section-editor.component';
import { FileEditorComponent } from 'src/app/components/editors/file-editor.component';
import { YearManagementComponent, YearCreatorDialog } from 'src/app/components/administration/year-management.component';
import { YearEditorComponent } from 'src/app/components/editors/year-editor.component';
import { ExportCSVComponent } from 'src/app/components/administration/export-csv.component';
import { GroupManagementComponent } from './components/administration/groups/group-management.component';
import { FileGroupEditorComponent } from './components/editors/file-group-editor.component';
import { BranchDisplayComponent } from './components/document-tree/branch-display.component';
import { Manage2FAComponent } from './components/authentication/manage-2fa.component';

import { forkJoin, of } from 'rxjs';

import { QrCodeModule } from 'ng-qrcode';
import { TotPInputComponent } from './components/authentication/totp-input.component';
import { FooterLoginComponent } from './components/authentication/footer-login.component';
import { AvatarCircleComponent } from './components/avatar-circle/avatar-circle.component';
import { Base64ToDataUrlPipe } from './pipes/base64-to-dataurl.pipe';
import { DataConfirmationComponent } from './components/authentication/data-confirmation.component';
import { DataProtectionComponent } from './components/single-page/data-protection.component';
import { ContactComponent } from './components/single-page/contact.component';
import { ImprintComponent } from './components/single-page/imprint.component';
import { IndependantLayoutComponent } from './components/layout/independant-layout.component';

import { UserDeleteConfirmation } from './components/editors/user-delete-confirmation-dialog.component';

import { Twig } from './classes/section';
import { LabelComponent } from './components/label/label.component';
import { ForgotPasswordComponent } from './components/forgot-password/forgot-password.component';
import { ChangeForgottenPasswordComponent } from './components/change-forgotten-password/change-forgotten-password.component';
import { ToastrModule } from 'ngx-toastr';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { CalendarComponent } from './components/calendar/calendar.component';
import { CalendarModule, DateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { EventEditorComponent } from './components/calendar/event-editor.component';
import { EventListComponent } from './components/calendar/event-list.component';
import { FileGroupCreatorDialog, FileGroupManagementComponent } from './components/administration/groups/file-group-management.component';
import { CalendarGroupCreatorDialog, CalendarGroupManagementComponent } from './components/administration/groups/calendar-group-management.component';
import { CalendarGroupEditorComponent } from './components/editors/calendar-group-editor.component';
import { NgxEditorModule } from 'ngx-editor';
import { RubricCreatorDialog, RubricManagementComponent } from './components/administration/groups/rubric-management.component';
import { RubricEditorComponent } from './components/editors/rubric-editor.component';
import { NewsComponent } from './components/news/news.component';
import { NewsEditorComponent } from './components/news/news-editor.component';
import { EventViewerComponent } from './components/calendar/event-viewer.component';
import { CalendarGroupSelectorComponent } from './components/calendar/calendar-group-selector.component';
import { RubricSelectorComponent } from './components/news/rubric-selector.component';
import { NewsPrintComponent } from './components/news/news-print.component';
import { StartComponent } from './components/start/start-component';
import { NewsDisplayComponent } from './components/news/news-display.component';
import { AuthAdminGuard } from './guards/auth-admin-guard';
import { AuthAdmin_Or_AdminMember_Guard } from './guards/auth-admin-or-admin-member-guard';
import { MatBadgeModule } from '@angular/material/badge';
import { AuthenticationService } from './services/authentication.service';
import { MyReCaptchaService } from './services/my-recaptcha.service';
import { UserCreatorComponent } from './components/editors/user-creator.component';
import { EventGridComponent } from './components/calendar/event-grid.component';
import { SubscribedEventListComponent } from './components/calendar/subscribed-event-list.component';
import { NewsViewerComponent } from './components/news/news-viewer.component';
import { DynamicFormComponent } from './components/dynamic-form/dynamic-form.component';
import { QuestionComponent } from './components/dynamic-form/question.component';
import { EventRegistrationComponent } from './components/calendar/event-registration.component';
import { GutBeratenComponent } from './components/calendar/gut-beraten.component';
import { EventRegistrationViewerComponent } from './components/calendar/event-registration-viewer.component';
import { DynamicFormReadOnlyComponent } from './components/dynamic-form/dynamic-form-readonly.component';
import { UnsubscribedEventListComponent } from './components/calendar/unsubscribed-event-list.component';
import { DocumentExchangeListComponent } from './components/document-exchange/document-exchange-list.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DocumentExchangeCreationComponent } from './components/document-exchange/document-exchange-creation.component';
import { DocumentExchangeCategoryCreatorDialog, DocumentExchangeCategoryManagementComponent } from './components/administration/groups/document-exchange-category-management.component';
import { DocumentExchangeCategoryEditorComponent } from './components/editors/document-exchange-category-editor.component';
import { MatPaginatorIntl, MatPaginatorModule } from '@angular/material/paginator';
import { getGermanPaginatorIntl } from './german-paginator';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DocumentExchangeCategorySelectorComponent } from './components/document-exchange/documentexchange-category-selector.component';
import { Extend2FAComponent } from './components/authentication/extend-2fa/extend-2fa.component';
import { PasswordInputComponent } from './components/password-input/password-input.component';
import { OfflineModeInterceptor } from './interceptors/offlineMode.interceptor';
import { ChatPrototypeService } from './services/chat-prototype.service';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import 'moment/locale/de';
import { FolderNameDialog } from './components/document-tree/folder-name-dialog.component';
import { ChangeAddressComponent } from './components/profile/change-address.component';
import { LastSaveMsgComponent } from './components/last-save-msg/last-save-msg.component';
import { EventUnsubscribeConfirmationDialog } from './components/editors/event-unsubscribe-confirmation-dialog.component';
import { MemberCreatorComponent } from './components/administration/member-creator.component';
import { TenantInterceptor } from "./interceptors/tenant.interceptor";
import { initTenantFactory, TenantInitializerProvider } from "./Initializer";
import { TenantContextService } from "./services/tenantcontext.service";
import { markEnvironmentReady } from "./environment.ready";
import { extractTenantFromLocation } from './core/tenant.util';
import { SubAppLoaderService } from './services/subapp-loader.service';
import { StyleService } from './services/style.service';
import {
  AuthenticationRegisterComponent
} from "./components/authentication/authentication-register/authentication-register.component";
import {
  AuthenticationPlansComponent
} from "./components/authentication/authentication-plans/authentication-plans.component";
import { PaymentsComponent } from './components/payments/payments.component';
import { LegalDocumentManagementComponent } from './components/administration/legal-documents-management.component';
import { LegalDocumentEditorComponent } from './components/administration/legal-document-editor.component';
import { CustomerService } from './services/customer.service';

@NgModule({
    declarations: [
        AppComponent,
    AuthenticationRegisterComponent,AuthenticationPlansComponent, PaymentsComponent,
    LayoutComponent, PageNotFoundComponent, FooterComponent,
        IndependantLayoutComponent, DataProtectionComponent, ContactComponent, ImprintComponent,
        AuthenticationLoginComponent, FooterLoginComponent, DataConfirmationComponent,
        ProfileComponent,
        ChangePasswordComponent,
        StatisticsComponent,
        DataMaintenanceComponent,
        MyMemberComponent,
        DocumentTreeComponent, BranchDisplayComponent, FolderNameDialog,
        UserChangePasswordDialog,
        Manage2FAComponent, TotPInputComponent,
        UserManagementComponent, UserEditorComponent, UserCreatorComponent, UserDeleteConfirmationDialog,
        MemberManagementComponent, UserDeleteConfirmation,
        SectionManagementComponent, SectionEditorComponent, SectionCreatorDialog,
        YearManagementComponent, YearEditorComponent, YearCreatorDialog,
        Base64ToDataUrlPipe,
        GroupManagementComponent,
        FileGroupManagementComponent, FileGroupEditorComponent, FileGroupCreatorDialog,
        CalendarGroupManagementComponent, CalendarGroupEditorComponent, CalendarGroupCreatorDialog,
        FileEditorComponent,
        ExportCSVComponent,
        LabelComponent,
        ForgotPasswordComponent,
        ChangeForgottenPasswordComponent,
        CalendarComponent, EventEditorComponent, EventGridComponent, EventListComponent, EventViewerComponent, SubscribedEventListComponent,
        CalendarGroupSelectorComponent,
        RubricManagementComponent, RubricEditorComponent, RubricCreatorDialog,
        EventUnsubscribeConfirmationDialog,
        RubricSelectorComponent,
        NewsComponent, NewsEditorComponent, NewsPrintComponent, NewsDisplayComponent, NewsViewerComponent,
        StartComponent,
        EventRegistrationComponent, DynamicFormComponent, QuestionComponent,
        EventRegistrationViewerComponent, DynamicFormReadOnlyComponent,
        GutBeratenComponent,
        UnsubscribedEventListComponent,
		DocumentExchangeListComponent, DocumentExchangeCreationComponent, DocumentExchangeCategoryManagementComponent,
		DocumentExchangeCategoryEditorComponent, DocumentExchangeCategoryCreatorDialog, DocumentExchangeCategorySelectorComponent,
		Extend2FAComponent, PasswordInputComponent,
		LegalDocumentManagementComponent, LegalDocumentEditorComponent
    ],
    bootstrap: [AppComponent],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        AppRoutingModule,
        TrivUIToolsLibModule,
        FormsModule, ReactiveFormsModule, MatFormFieldModule,
        MatButtonModule, MatInputModule,
        MatCardModule,
        MatToolbarModule, MatSidenavModule,
        MatMenuModule,
        MatIconModule,
        MatListModule,
        MatProgressBarModule, MatProgressSpinnerModule,
        MatSnackBarModule,
        MatTabsModule,
        MatTableModule,
        MatSortModule,
        MatCheckboxModule,
        MatSelectModule,
        MatRippleModule,
        MatDialogModule,
        MatRadioModule,
        QrCodeModule,
        ToastrModule.forRoot(),
        CalendarModule.forRoot({ provide: DateAdapter, useFactory: adapterFactory }),
        NgxEditorModule.forRoot({
            locals: {
                // menu
                bold: 'Fett',
                italic: 'Kursiv',
                code: 'Code',
                blockquote: 'Blockquote',
                underline: 'Unterstrichen',
                strike: 'Durchgestrichen',
                bullet_list: 'Aufzählungszeichen',
                ordered_list: 'Nummerierung',
                heading: 'Heading',
                h1: 'Header 1',
                h2: 'Header 2',
                h3: 'Header 3',
                h4: 'Header 4',
                h5: 'Header 5',
                h6: 'Header 6',
                align_left: 'Linksbündig',
                align_center: 'Zentriert ausrichten',
                align_right: 'Rechtsbündig',
                align_justify: 'Blocksatz',
                text_color: 'Schriftfarbe',
                background_color: 'Hervorheben',
                // popups, forms, others...
                url: 'URL',
                text: 'Text',
                openInNewTab: 'In neuem Tab öffnen',
                insert: 'Einfügen',
                altText: 'Alt Text',
                title: 'Title',
                remove: 'Löschen',
            },
        }),
        MatBadgeModule,
		MatDatepickerModule,
		MatPaginatorModule,
        MatTooltipModule,
        AvatarCircleComponent,
		ChangeAddressComponent,
		LastSaveMsgComponent,
        MemberCreatorComponent,
        MemberEditorComponent
    ],
    exports: [
    ],
    providers: [
        { provide: 'environment', useValue: environment },

        { provide: LocationStrategy, useClass: HashLocationStrategy },
        { provide: APP_INITIALIZER, useFactory: initApp, multi: true, deps: [HttpClient, AuthenticationService, MyReCaptchaService, ChatPrototypeService, SubAppLoaderService, StyleService, CustomerService] },

        { provide: LOCALE_ID, useValue: 'de-DE' },
        { provide: 'LOCALSTORAGE', useValue: window.localStorage },
        AuthBasicGuard, AuthAdminGuard, AuthAdmin_Or_AdminMember_Guard,

        { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: OfflineModeInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: TenantInterceptor, multi: true },
        { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: 2500 } },
        provideHttpClient(withInterceptorsFromDi()),
        // dein Initializer

        TenantInitializerProvider,
        // provideNativeDateAdapter(),
        provideMomentDateAdapter(),
        { provide: MAT_DATE_LOCALE, useValue: 'de-DE' },
        { provide: MatPaginatorIntl, useValue: getGermanPaginatorIntl() }
    ]
})
export class AppModule { }

function safeParse<T>(s?: string | null): T | undefined { try { return s ? JSON.parse(s) as T : undefined; } catch { return undefined; } }


export function initApp(
  httpClient: HttpClient,
  authenticationService: AuthenticationService,
  recaptchaService: MyReCaptchaService,
  chatPrototypeService: ChatPrototypeService,
  subAppLoader: SubAppLoaderService,
  styleService: StyleService,
  customerService: CustomerService
) {
  return () => {
    return new Promise((resolve) => {
      forkJoin({
        environment: httpClient
          .get("assets/config.json")
          .pipe(catchError(() => httpClient.get("assets/json/environment.json"))),
        dataMaintenance: httpClient.get("assets/json/forms/data-maintenance.json"),
        file: httpClient.get("assets/json/forms/file.json"),
        member: httpClient.get("assets/json/forms/member.json"),
        section: httpClient.get("assets/json/forms/section.json"),
        user: httpClient.get("assets/json/forms/user.json"),
        year: httpClient.get("assets/json/forms/year.json"),
        group: httpClient.get("assets/json/forms/group.json"),
        calendarEvent: httpClient.get("assets/json/forms/calendar-event.json"),
        news: httpClient.get("assets/json/forms/news.json"),
        subsections: httpClient.get("assets/json/subsections.json"),

        // NEU aus V2: Defaults aus Assets (werden später ggf. vom Backend überschrieben)
        features: httpClient.get("assets/json/features.json").pipe(catchError(() => of(null))),
        texts: httpClient.get("assets/json/texts.json").pipe(catchError(() => of(null))),
      })
        .pipe(
          catchError((err) => {
            alert("An error occurred while loading the json files");
            console.error("error in initApp while loading .json files", err);
            return of(null);
          }),
          switchMap((results) => {
            if (!results) return of(false);

            const env = results.environment as Environment;

            environment.backendUrl = env.backendUrl;

            environment.dataMaintenance = results.dataMaintenance as DataInput[];
            environment.file = results.file as DataInput[];
            environment.member = results.member as DataInput[];
            environment.section = results.section as DataInput[];
            environment.user = results.user as DataInput[];
            environment.year = results.year as DataInput[];
            environment.group = results.group as DataInput[];
            environment.calendarEvent = results.calendarEvent as DataInput[];
            environment.news = results.news as DataInput[];

            // Defaults aus Assets (nur setzen, wenn vorhanden; Backend kann später überschreiben)
            if (results.features) {
              environment.features = results.features as EnvironmentFeatures;
            }
            if (results.texts) {
              environment.texts = results.texts as Texts;
            }

            const subsections = results.subsections as {
              id: string;
              label: string;
              currency: boolean;
              info: string;
            }[];
            for (const subsection of subsections) {
              environment.twigs.push(
                new Twig(subsection.label, subsection.id, subsection.currency, subsection.info)
              );
            }

            const tenant = extractTenantFromLocation(); // Subdomain: <tenant>.<domain>
            if (!tenant) {
              console.error("Kein Tenant aus der Subdomain ermittelt, App-Initialisierung abgebrochen");
              return of(false);
            }
            window.localStorage.setItem("tenantContext", tenant);

            const url = `${environment.backendUrl}kunde/${encodeURIComponent(tenant)}`;
            return httpClient.get(url).pipe(
              map((dto: any) => {
                // Styles anwenden (V1)
                const styleCfg = safeParse<any>(dto?.kundenStyle);
                if (styleCfg) styleService.applyStyleConfig(styleCfg);

                // Features & Texte aus Backend (überschreiben ggf. die Defaults aus Assets)
                const f = safeParse<Partial<EnvironmentFeatures>>(dto?.kundenFeatures);
                if (f)
                  environment.features = Object.assign(
                    new EnvironmentFeatures(),
                    environment.features,
                    f
                  );

                const t = safeParse<Texts>(dto?.kundenTexts);
                if (t) environment.texts = t;

                // SubApps & Version (V1)
                const subAppsCfg = safeParse<{ version?: string; microFrontends?: any[] }>(
                  dto?.kundenSubApplications
                );
                if (subAppsCfg?.version) {
                  environment.version = subAppsCfg.version ?? environment.version;
                }
                if (subAppsCfg?.microFrontends?.length) {
                  subAppLoader.loadFromConfig(subAppsCfg);
                }

                window.localStorage.setItem("kundenCache", JSON.stringify(dto));
                window.localStorage.setItem("customerContextPreloaded", "1");
                return true;
              }),
              catchError((err) => {
                console.warn("Kundenkontext konnte nicht geladen werden, fahre mit Defaults fort", err);
                window.localStorage.removeItem("customerContextPreloaded");
                return of(true);
              }),
				switchMap( () => {
					return customerService.loadLogo().pipe(
						catchError( err => {
							console.warn( "Logo loading via CustomerService failed, continuing without", err );
							return of(true);
						})
					);
				}),
              // Danach wie in V1: Offline/Chat/Recaptcha
              switchMap(() => {
                environment.chatPrototype = env.chatPrototype;
                environment.offlineMode = !!env.offlineMode;
                environment.offlineModeAdmin = !!env.offlineModeAdmin || false;
                environment.backendUrl = env.backendUrl;
                environment.stripePublishableKey = env.stripePublishableKey ?? '';
                environment.stripeSuccessUrl = env.stripeSuccessUrl ?? '';
                environment.stripeCancelUrl = env.stripeCancelUrl ?? '';

                if (env.offlineMode && env.chatPrototype) {
                  chatPrototypeService.generateProtoData(authenticationService.getCurrentUser());
                }

                environment.recaptchaKey = env.recaptchaKey;
                environment.recaptchaActive = env.recaptchaActive;

                if (environment.recaptchaActive) {
                  return recaptchaService.initialize().pipe(
                    catchError((err) => {
                      alert("An error occurred while initializing ReCaptcha");
                      console.error("error in initApp while initializing ReCaptcha", err);
                      return of(false);
                    })
                  );
                } else {
                  console.log("ReCaptcha initialization skipped due to inactive state");
                  return of(true);
                }
              })
            );
          })
        )
        .subscribe((b) => {
          markEnvironmentReady(); // in V1 vorhanden, in V2 gefehlt
          if (!b) {
            window.localStorage.removeItem("currentUser");
            resolve(false);
            return;
          }

          if (environment.offlineMode) resolve(true);
          else if (authenticationService.checkPreviousLogin()) {
            authenticationService.initializeAppAfterAuth().subscribe((b2) => {
              resolve(b2);
            });
          } else resolve(true);
        });
    });
  };
}
